import { Reveal } from "./Reveal";
import { Button } from "@/components/ui/button";
import { Check, Users, User } from "lucide-react";
import { leilaine } from "@/lib/images";
import { Picture } from "./Picture";
import { trackCtaClick } from "@/lib/analytics";

const individualIncludes = [
  "4 sessões ao vivo de 1h30, só sobre a sua empresa",
  "O mapa da sua operação, feito com você",
  "Claude Code instalado e configurado no seu computador",
  "Arquitetura de pastas e memória da empresa — sua, e não de uma IA",
  "Agentes e skills criados para a sua necessidade",
  "Conectores com as ferramentas que você já usa",
  "Comunidade com apoio por WhatsApp",
  "30% de desconto nos eventos presenciais e 80% nos online da ZNITH.AI",
];

const grupoIncludes = [
  "Mesmas 4 sessões, aplicadas à operação da sua equipe",
  "Time inteiro construindo junto, no mesmo padrão",
  "Escopo e agenda desenhados na conversa inicial",
  "Valor sob consulta, conforme número de pessoas",
];

export function PricingSection() {
  return (
    <section id="investimento" className="relative overflow-hidden scroll-mt-20 py-12 md:py-16">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_color-mix(in_oklab,_var(--gold)_8%,_transparent)_0%,_transparent_60%)]" />
      <div className="container-narrow">
        <Reveal y={24} duration={0.7}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="mb-4 text-[11px] uppercase tracking-[0.32em] text-gold">Investimento</p>
          <h2 className="text-balance text-3xl leading-tight text-ivory md:text-5xl">
            Dois formatos. A mesma construção ao vivo.
          </h2>
          <p className="mt-5 text-pretty text-base text-slate-muted md:text-lg">
            Individual, se a operação é sua. Grupo fechado, se você quer o time inteiro no mesmo padrão.
          </p>
        </Reveal>

        <div className="mx-auto mt-10 max-w-2xl">
          {/* Individual */}
          <Reveal y={24} duration={0.6}
            className="glass-card relative overflow-hidden rounded-3xl border-gold/50 p-8 shadow-elegant ring-1 ring-gold/25 md:p-10"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-midnight/60 px-3 py-1">
              <User className="h-3 w-3 text-gold" />
               <span className="text-[10px] uppercase tracking-[0.3em] text-gold-soft">Sprint Code Individual · serviço premium</span>
            </div>

            <p className="mt-6 text-xs uppercase tracking-[0.25em] text-slate-muted">Investimento parcelado</p>
            <p className="mt-2 flex items-baseline gap-2">
              <span className="text-5xl text-ivory md:text-6xl" style={{ fontFamily: "var(--font-display)" }}>
                 12×
              </span>
               <span className="text-3xl text-gold md:text-4xl">R$ 596,33</span>
            </p>
            <p className="mt-1 text-xs text-slate-muted">no cartão de crédito</p>

            <div className="mt-5 border-t border-border/60 pt-4">
              <p className="text-sm text-slate-muted">
                 ou <span className="text-ivory/80">R$ 6.870,00 à vista no Pix — chave znith@campioto.com</span>
              </p>
            </div>

            <ul className="mt-7 space-y-3 border-t border-border/60 pt-6 text-sm text-ivory/90">
              {individualIncludes.map((i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>

            <Button
              asChild
              size="lg"
              className="mt-9 h-14 w-full bg-gradient-gold text-base text-primary-foreground hover:opacity-90"
            >
               <a href="#inscricao" onClick={() => trackCtaClick("pricing_individual", "Quero meu Sprint Code")}>
                 Quero meu Sprint Code
              </a>
            </Button>

            <p className="mt-4 text-center text-xs text-slate-muted">
               Após o pagamento: conversa de 20 minutos com a Leilaine para definir a agenda das 4 sessões e a área que vai para o mapa. O plano do Claude é à parte: o básico sai por volta de R$ 110 por mês.
            </p>
          </Reveal>
        </div>

        {/* Grupo fechado — faixa secundária */}
        <Reveal y={20} duration={0.6} delay={0.1}
          className="mx-auto mt-6 max-w-4xl rounded-2xl border border-border bg-card/40 p-6 md:p-8"
        >
          <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
            <div className="min-w-0">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border bg-midnight/40 px-3 py-1">
                <Users className="h-3 w-3 shrink-0 text-slate-muted" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-slate-muted">
                  Grupo fechado · sob consulta
                </span>
              </div>
              <h3 className="mt-4 text-xl text-ivory md:text-2xl">Quer o time inteiro no mesmo padrão?</h3>
              <ul className="mt-4 grid gap-2 text-sm text-slate-muted sm:grid-cols-2">
                {grupoIncludes.map((i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" />
                    <span className="min-w-0">{i}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 w-full shrink-0 border-gold/40 bg-transparent text-sm text-ivory hover:bg-gold/10 hover:text-gold-soft md:w-auto md:px-8"
            >
              <a href="#inscricao" onClick={() => trackCtaClick("pricing_grupo", "Falar sobre grupo fechado")}>
                Falar sobre grupo fechado
              </a>
            </Button>
          </div>
        </Reveal>

        {/* Sobre Leilaine */}
        <Reveal y={24} duration={0.7} delay={0.2}
          className="mx-auto mt-10 max-w-4xl rounded-2xl border border-border/60 bg-card/40 p-6 md:p-10"
        >
          <div className="grid gap-8 md:grid-cols-[minmax(0,_220px)_1fr] md:items-start md:gap-10">
            <div className="mx-auto w-full max-w-[240px] md:mx-0 md:max-w-none">
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-xl border border-gold/20 shadow-elegant">
                <Picture
                  pic={leilaine}
                  alt="Leilaine, fundadora da ZNITH.AI"
                  sizes="240px"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div>
              <p className="mb-3 text-[11px] uppercase tracking-[0.3em] text-gold">Sobre Leilaine</p>
              <h3 className="mb-4 text-2xl text-ivory md:text-3xl">Fundadora da ZNITH.AI</h3>
              <p className="mb-3 leading-relaxed text-slate-muted">
                Nos últimos anos, trabalhou com empresas de nichos completamente diferentes — clínicas, distribuidoras,
                indústria, prestadores de serviço — aplicando o <span className="text-ivory/90">Protocolo Z3</span>:
                mapeamento da operação, configuração de IA e desenvolvimento de líderes para pensar em sistemas, não em
                tarefas.
              </p>
              <p className="mb-3 leading-relaxed text-slate-muted">
                Entre as empresas que passaram pelo processo estão{" "}
                <span className="text-ivory/90">
                  clínicas, distribuidoras e prestadores de serviço que bateram metas que estavam travadas e encontraram
                  clareza operacional que não conseguiam sozinhos
                </span>
                .
              </p>
              <p className="leading-relaxed text-slate-muted">
                <span className="text-ivory/90">
                   O SPRINT CODE é a forma mais direta de trazer esse processo para dentro da sua empresa — agora
                  sessão a sessão, construído junto com você.
                </span>
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
